/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Plus, Search } from 'lucide-react';
import { Collapse } from 'antd';
import { Button as CrcButton, Checkbox, ConfirmationModal } from '@crc-evans/ui-components';
import { useDispatch, useSelector } from 'react-redux';
import { updateDprSkipState } from '../../redux/dpr/action';
import ProductivityMainlinePanel, {
  invalidateDprProductivityCachesForLog,
} from './ProductivityMainlinePanel';
import axiosConfig from '../../services/axiosConfig';
import { isOffshoreProject } from '../../utils/projectConfig';

const { Panel } = Collapse;

const getMainlineDisplayTitle = (ml) =>
  ml.pipelineSection ||
  ml.name ||
  (ml.pipeStringId != null && ml.pipeStringId !== ''
    ? `Mainline ${ml.pipeStringId}`
    : `Mainline ${ml.id}`);

/**
 * Collapse row: mainline name on the left; Skip mainline (+ Add Weld Details onshore) on the right.
 * Clicks on checkbox/button must not toggle the panel — only the collapse arrow expands.
 */
const ProductivityMainlineCollapseHeader = ({
  pipelineTitle,
  getPanelApi,
  skipMainline,
  onSkipMainlineChange,
  isOffshore = false,
}) => {
  const stopPanelToggle = (e) => {
    if (e && typeof e.stopPropagation === 'function') {
      e.stopPropagation();
    }
    if (e && typeof e.stopImmediatePropagation === 'function') {
      e.stopImmediatePropagation();
    }
  };
  return (
    <div
      role="presentation"
      onClick={stopPanelToggle}
      onMouseDown={stopPanelToggle}
      style={{
        display: 'flex',
        alignItems: 'center',
        flex: 1,
        minWidth: 0,
        width: '100%',
        justifyContent: 'space-between',
        gap: '16px',
        flexWrap: 'wrap',
      }}
    >
      <span
        style={{
          fontWeight: 600,
          color: '#0F172A',
          fontSize: '15px',
          lineHeight: 1.3,
          flex: '1 1 auto',
          minWidth: 0,
          textAlign: 'left',
          paddingRight: '12px',
        }}
      >
        Mainline: {pipelineTitle}
      </span>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '12px',
          flexWrap: 'wrap',
          flexShrink: 0,
          marginLeft: 'auto',
        }}
      >
        <Checkbox
          label="Skip mainline"
          checked={skipMainline}
          onChange={(e) => {
            stopPanelToggle(e);
            const checked = e?.target ? e.target.checked : e;
            onSkipMainlineChange(checked);
          }}
        />
        {!isOffshore && (
          <CrcButton
            label="Add Weld Details"
            variant="primary"
            size="small"
            icon={Plus}
            onClick={(ev) => {
              stopPanelToggle(ev);
              getPanelApi()?.openAddWeld?.();
            }}
            style={{ backgroundColor: '#F43F5E', borderColor: '#F43F5E' }}
          />
        )}
      </div>
    </div>
  );
};

/**
 * One DPR log per day (`dprLogId`); productivity still edits welds per mainline (`mainlineProductivity` on save).
 * Multiple mainlines: collapsible cards (collapsed by default). All panels share the same `productivityData`
 * from the day-level DPR log GET.
 */
const ProductivityLog = ({
  productivityData,
  onSave,
  isSubmitting = false,
  shift = 'day',
  dprLogId,
  sowId,
  selectedProject,
  selectedPipeline,
  isLastDprSection,
  /** All SOWs (mainlines) for the project — from DPR page pipeline list */
  projectMainlines = null,
  /** Incremented when the user opens the Productivity tab — triggers GET /dpr-productivity (once + cache for other mainlines). */
  refreshToken = 0,
  isShiftSkipped = false,
}) => {
  const panelApisRef = useRef({});
  const singlePanelRef = useRef(null);
  const dispatch = useDispatch();
  const [globalSkipConfirmOpen, setGlobalSkipConfirmOpen] = useState(false);
  const [skipConfirmSowId, setSkipConfirmSowId] = useState(null);
  const [mainlineSearch, setMainlineSearch] = useState('');
  const [activeCollapseKeys, setActiveCollapseKeys] = useState([]);
  const [isGlobalSaving, setIsGlobalSaving] = useState(false);
  const mainlines = Array.isArray(projectMainlines) ? projectMainlines : [];
  const isOffshore = isOffshoreProject(selectedProject);
  const skipStatesBySection = useSelector((state) => state.dprReducer?.skipStatesBySection ?? {});

  const sortedMainlines = useMemo(
    () =>
      [...mainlines].sort((a, b) =>
        getMainlineDisplayTitle(a).localeCompare(getMainlineDisplayTitle(b), undefined, {
          sensitivity: 'base',
        })
      ),
    [mainlines]
  );

  const searchTerm = mainlineSearch.trim().toLowerCase();

  const filteredMainlines = useMemo(() => {
    if (!searchTerm) return sortedMainlines;
    return sortedMainlines.filter((ml) =>
      getMainlineDisplayTitle(ml).toLowerCase().includes(searchTerm)
    );
  }, [sortedMainlines, searchTerm]);

  const visibleMainlineIds = useMemo(
    () => new Set(filteredMainlines.map((ml) => String(ml.id))),
    [filteredMainlines]
  );

  const allMainlinesProductivitySkipped = useMemo(
    () =>
      mainlines.length > 0 &&
      mainlines.every((ml) => Boolean(skipStatesBySection[ml.id]?.skipProductivity)),
    [mainlines, skipStatesBySection]
  );

  const skipConfirmMainline = useMemo(
    () => (skipConfirmSowId != null ? mainlines.find((ml) => String(ml.id) === String(skipConfirmSowId)) : null),
    [skipConfirmSowId, mainlines]
  );

  /** Use card layout even for a single mainline (consistent UI). */
  const useCardLayout = mainlines.length >= 1 && selectedPipeline?.id != null;

  const mainlineIdsKey = useMemo(
    () => mainlines.map((m) => m.id).filter((id) => id != null).join(','),
    [mainlines]
  );

  useEffect(() => {
    if (!refreshToken || !dprLogId) return;

    /** Panel refs attach after Collapse/header paint; retry until imperative API is reachable. */
    let cancelled = false;
    let attempt = 0;
    const maxAttempts = 30;
    const delayMs = 50;

    /** Tab click already ran GET /dpr-productivity + filled cache; only sync panel state (no cache bust). */
    const runRefresh = async () => {
      if (!useCardLayout) {
        const fn = singlePanelRef.current?.reloadProductivityData;
        if (typeof fn !== 'function') return false;
        return (await fn()) === true;
      }
      if (mainlines.length === 0) return true;
      const apis = mainlines.map((ml) => panelApisRef.current?.[ml.id]);
      if (apis.some((api) => !api || typeof api.reloadProductivityData !== 'function')) return false;
      for (let i = 0; i < apis.length; i += 1) {
        await apis[i].reloadProductivityData();
      }
      return true;
    };

    const loop = async () => {
      await new Promise((resolve) => {
        requestAnimationFrame(() => resolve());
      });
      while (!cancelled && attempt < maxAttempts) {
        try {
          /* eslint-disable no-await-in-loop -- sequential retries until refs exist */
          if (await runRefresh()) return;
        } catch {
          /* keep retrying */
        }
        attempt += 1;
        await new Promise((resolve) => {
          setTimeout(resolve, delayMs);
        });
      }
    };

    void loop();

    return () => {
      cancelled = true;
    };
  }, [refreshToken, dprLogId, shift, useCardLayout, mainlineIdsKey]);

  const handleGlobalSave = async () => {
    if (isShiftSkipped) return;
    if (!dprLogId) return;
    setIsGlobalSaving(true);
    try {
      const parts = mainlines
        .map((ml) => panelApisRef.current?.[ml.id]?.getMainlineDailyWeldLogDraft?.())
        .filter(Boolean);
      const weldLogMetrics = mainlines.flatMap((ml) => {
        const metrics = panelApisRef.current?.[ml.id]?.getWeldLogMetricsDraft?.();
        return Array.isArray(metrics) ? metrics : [];
      });
      // Notes live on each `mainlineProductivity[]` item (`notes` from each panel), not on root `dprProductivity`.
      const payload = {
        shift,
        dprProductivity: {
          dprLogId,
          ...(weldLogMetrics.length > 0 ? { weldLogMetrics } : {}),
          mainlineProductivity: parts,
        },
      };
      await axiosConfig.put('/dpr-productivity', payload);
      invalidateDprProductivityCachesForLog(dprLogId, shift);
      const apis = mainlines.map((ml) => panelApisRef.current?.[ml.id]);
      if (apis[0]?.refreshFromServer) {
        await apis[0].refreshFromServer();
        for (let i = 1; i < apis.length; i += 1) {
          if (typeof apis[i]?.reloadProductivityData === 'function') await apis[i].reloadProductivityData();
        }
      }
      // IMPORTANT: do not auto-navigate to next section for now.
    } finally {
      setIsGlobalSaving(false);
    }
  };

  const handlePerMainlineSkipChange = (mlId, checked) => {
    if (!mlId) return;
    if (!checked) {
      dispatch(updateDprSkipState(mlId, 'skipProductivity', false));
      return;
    }
    setSkipConfirmSowId(mlId);
  };

  if (!useCardLayout) {
    return (
      <ProductivityMainlinePanel
        ref={singlePanelRef}
        productivityData={productivityData}
        onSave={onSave}
        isSubmitting={isSubmitting}
        shift={shift}
        dprLogId={dprLogId}
        sowId={sowId}
        selectedProject={selectedProject}
        selectedPipeline={selectedPipeline}
        isLastDprSection={isLastDprSection}
        isShiftSkipped={isShiftSkipped}
        compactChrome={false}
      />
    );
  }

  return (
    <div style={{ width: '100%', paddingBottom: '72px' }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: '12px',
          marginBottom: '16px',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            flex: '0 1 280px',
            maxWidth: '280px',
            minWidth: '180px',
            padding: '6px 10px',
            backgroundColor: '#FFFFFF',
            borderRadius: '8px',
            border: '1px solid #E5E7EB',
          }}
        >
          <Search size={14} color="#9CA3AF" style={{ flexShrink: 0 }} />
          <input
            value={mainlineSearch}
            onChange={(e) => setMainlineSearch(e.target.value)}
            placeholder="Search mainlines..."
            style={{
              flex: 1,
              border: 'none',
              outline: 'none',
              fontSize: '13px',
              color: '#1F2937',
              background: 'transparent',
              minWidth: 0,
            }}
          />
        </div>
        <Checkbox
          label="Skip productivity (all mainlines)"
          checked={allMainlinesProductivitySkipped}
          onChange={(e) => {
            const checked = e?.target ? e.target.checked : e;
            if (!checked) {
              mainlines.forEach((ml) => {
                dispatch(updateDprSkipState(ml.id, 'skipProductivity', false));
              });
              return;
            }
            setGlobalSkipConfirmOpen(true);
          }}
        />
      </div>

      {filteredMainlines.length === 0 ? (
        <div
          style={{
            padding: '24px 16px',
            textAlign: 'center',
            color: '#6B7280',
            fontSize: '14px',
          }}
        >
          No mainlines match your search.
        </div>
      ) : (
        <Collapse
          bordered={false}
          className="productivity-mainline-collapse"
          activeKey={activeCollapseKeys}
          onChange={(keys) => setActiveCollapseKeys(Array.isArray(keys) ? keys : [keys])}
          destroyInactivePanel={false}
        >
          {sortedMainlines.map((ml) => {
            const key = String(ml.id);
            const title = getMainlineDisplayTitle(ml);
            const isVisible = visibleMainlineIds.has(key);
            const skipMainline = Boolean(skipStatesBySection[ml.id]?.skipProductivity);

            return (
              <Panel
                key={key}
                className={isVisible ? undefined : 'productivity-mainline-panel-hidden'}
                header={
                  <ProductivityMainlineCollapseHeader
                    pipelineTitle={title}
                    getPanelApi={() => panelApisRef.current[ml.id]}
                    skipMainline={skipMainline}
                    onSkipMainlineChange={(checked) => handlePerMainlineSkipChange(ml.id, checked)}
                    isOffshore={isOffshore}
                  />
                }
              >
                <ProductivityMainlinePanel
                  ref={(node) => {
                    if (node) panelApisRef.current[ml.id] = node;
                    else delete panelApisRef.current[ml.id];
                  }}
                  key={`${ml.id}-${shift}`}
                  productivityData={
                    productivityData && typeof productivityData === 'object' ? productivityData : {}
                  }
                  onSave={onSave}
                  isSubmitting={isSubmitting}
                  shift={shift}
                  dprLogId={dprLogId}
                  sowId={ml.id}
                  selectedProject={selectedProject}
                  selectedPipeline={ml}
                  mainlineTitle={title}
                  isLastDprSection={isLastDprSection}
                  isShiftSkipped={isShiftSkipped}
                  compactChrome
                  hideFooterActions
                />
              </Panel>
            );
          })}
        </Collapse>
      )}

      {/* Save / Save & Next — sticky at bottom (one API call for all mainlines) */}
      <div
        style={{
          position: 'sticky',
          bottom: '24px',
          display: 'flex',
          justifyContent: 'flex-end',
          marginTop: '24px',
          paddingTop: '16px',
          zIndex: 10,
        }}
      >
        <CrcButton
          label={
            typeof isLastDprSection === 'boolean'
              ? isLastDprSection
                ? 'Save section'
                : 'Save & Next'
              : 'Save'
          }
          variant="secondary"
          onClick={handleGlobalSave}
          disabled={isSubmitting || isGlobalSaving || isShiftSkipped}
          style={{ height: '42px', padding: '0 32px', fontSize: '14px', fontWeight: 600 }}
        />
      </div>

      <ConfirmationModal
        title="Skip productivity (all mainlines)"
        message={`Are you sure you want to skip the Productivity log for all ${mainlines.length} mainline sections for this ${shift === 'night' ? 'night' : 'day'} shift?`}
        confirmText="Confirm"
        cancelText="Cancel"
        isOpen={globalSkipConfirmOpen}
        onConfirm={() => {
          mainlines.forEach((ml) => {
            dispatch(updateDprSkipState(ml.id, 'skipProductivity', true));
          });
          setGlobalSkipConfirmOpen(false);
        }}
        onCancel={() => setGlobalSkipConfirmOpen(false)}
        size="medium"
      />

      <ConfirmationModal
        title="Skip mainline"
        message={`Are you sure you want to skip the Productivity log for mainline "${skipConfirmMainline ? getMainlineDisplayTitle(skipConfirmMainline) : ''}" for this ${shift === 'night' ? 'night' : 'day'} shift?`}
        confirmText="Confirm"
        cancelText="Cancel"
        isOpen={skipConfirmSowId != null}
        onConfirm={() => {
          if (skipConfirmSowId != null) {
            dispatch(updateDprSkipState(skipConfirmSowId, 'skipProductivity', true));
          }
          setSkipConfirmSowId(null);
        }}
        onCancel={() => setSkipConfirmSowId(null)}
        size="medium"
      />
    </div>
  );
};

export default ProductivityLog;
